// NewSidebar/usePermissionEngine.js
import { useMemo } from "react";

/* ─────────────────────────────────────────────
   1. ساخت Map بهینه از Claims  →  O(1) lookup
   ───────────────────────────────────────────── */
function buildClaimsMap(claims) {
  // Map<controllerName, Set<actionName>>
  const map = new Map();
  if (!claims || !Array.isArray(claims)) return map;

  claims.forEach((ctrl) => {
    const actionSet = new Set();
    ctrl.actionDetails?.forEach((a) => {
      // actionName فرمت ":Controller:ActionName" دارد
      const parts = a.actionName?.split(":", 3);
      if (parts && parts[2]) {
        actionSet.add(parts[2]);
      }
    });
    map.set(ctrl.controllerName, actionSet);
  });

  return map;
}

/* ─────────────────────────────────────────────
   2. بررسی یک بلاک permission واحد
   ───────────────────────────────────────────── */
function checkSinglePermission(perm, claimsMap) {
  // آیا Controller اصلاً وجود دارد؟
  const actionSet = claimsMap.get(perm.controller);
  if (!actionSet) return false;

  // اگر actions تعریف نشده → فقط وجود Controller کافی‌ست
  if (!perm.actions || perm.actions.length === 0) return true;

  // بررسی actions با منطق AND/OR
  const logic = (perm.actionsLogic || "OR").toUpperCase();

  if (logic === "AND") {
    return perm.actions.every((act) => actionSet.has(act));
  }
  // OR (پیش‌فرض)
  return perm.actions.some((act) => actionSet.has(act));
}

/* ─────────────────────────────────────────────
   3. بررسی آرایه permissions یک آیتم منو
   ───────────────────────────────────────────── */
function checkPermissions(item, claimsMap, controllerSet) {
  const perms = item.permissions;

  // permissions خالی یا undefined → همه ببینند
  if (!perms || perms.length === 0) return true;

  const logic = (item.permissionLogic || "AND").toUpperCase();

  if (logic === "AND") {
    return perms.every((p) => checkSinglePermission(p, claimsMap));
  }
  // OR
  return perms.some((p) => checkSinglePermission(p, claimsMap));
}

/* ─────────────────────────────────────────────
   4. بررسی excludeRoles
   ───────────────────────────────────────────── */
function checkExcludeRoles(item, userRole) {
  if (!item.excludeRoles || item.excludeRoles.length === 0) return true;
  return !item.excludeRoles.includes(userRole);
}

/* ─────────────────────────────────────────────
   5. بررسی excludeControllers
      (مثال: FormAfzar فقط اگر OrderOnline نباشد)
   ───────────────────────────────────────────── */
function checkExcludeControllers(item, controllerSet) {
  if (!item.excludeControllers || item.excludeControllers.length === 0)
    return true;
  // اگر هر کدام از Controller‌های exclude وجود داشت → مخفی شود
  return !item.excludeControllers.some((c) => controllerSet.has(c));
}

/* ─────────────────────────────────────────────
   6. فیلتر بازگشتی منو
   ───────────────────────────────────────────── */
function filterMenu(items, claimsMap, controllerSet, userRole) {
  if (!items) return [];

  return items.reduce((acc, item) => {
    // 1) بررسی excludeRoles
    if (!checkExcludeRoles(item, userRole)) return acc;

    // 2) بررسی excludeControllers
    if (!checkExcludeControllers(item, controllerSet)) return acc;

    // 3) بررسی permissions خود آیتم
    const selfAllowed = checkPermissions(item, claimsMap, controllerSet);

    // --- اگر children دارد (parent / submenu) ---
    if (item.children && item.children.length > 0) {
      const filteredChildren = filterMenu(
        item.children,
        claimsMap,
        controllerSet,
        userRole
      );

      // Parent هوشمند: اگر هیچ child مجازی نماند → parent هم مخفی
      if (filteredChildren.length === 0) return acc;

      acc.push({ ...item, children: filteredChildren });
      return acc;
    }

    // --- آیتم ساده (بدون children) ---
    if (selfAllowed) {
      acc.push(item);
    }

    return acc;
  }, []);
}

/* ═════════════════════════════════════════════
   هوک اصلی
   ═════════════════════════════════════════════ */
export default function usePermissionEngine(menuData) {
  const filteredMenu = useMemo(() => {
    // -------- خواندن Claims از localStorage --------
    let claims = [];
    try {
      claims = JSON.parse(localStorage.getItem("claims")) || [];
    } catch {
      claims = [];
    }

    // -------- خواندن userRole --------
    let userRole = "";
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      userRole = userInfo?.userRole?.[0]?.roleName || "";
    } catch {
      userRole = "";
    }

    // -------- ساخت ساختارهای بهینه --------
    const claimsMap = buildClaimsMap(claims);

    // Set ساده از نام Controller‌ها (برای excludeControllers)
    const controllerSet = new Set(claimsMap.keys());

    // -------- فیلتر کردن --------
    return filterMenu(menuData, claimsMap, controllerSet, userRole);
  }, [menuData]);

  return filteredMenu;
}
